import type { AssignmentResponseWithDetails } from '@repo/types/assignments';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCommentAssignmentSubmission } from '@repo/api/assignment/useCommentAssignmentSubmission';
import { type AssignmentFeedbackSchema, type AssignmentFeedbackSchemaInput, assignmentFeedbackSchema } from '@repo/schemas/assignment/assignmentSubmission';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { AttachmentPreviewButton } from '@/components/features/files/attachment-card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tiptap } from '@/components/ui/tiptap-editor';
import { UndoRedoButton } from '@/components/ui/tiptap/tiptap-ui';
import { ListDropdownMenu } from '@/components/ui/tiptap/tiptap-ui/list-dropdown-menu';
import { MarkButton } from '@/components/ui/tiptap/tiptap-ui/mark-button';
import { simpleExtensionSet } from '@/lib/tiptap-extensions-sets';

interface AssignmentFeedbackFormProps {
  assignmentId: number;
  submission: AssignmentResponseWithDetails;
}

export function AssignmentFeedbackForm({ assignmentId, submission }: AssignmentFeedbackFormProps) {
  const { mutateAsync, isPending, errorMessage } = useCommentAssignmentSubmission();

  const form = useForm<AssignmentFeedbackSchemaInput, unknown, AssignmentFeedbackSchema>({
    resolver: standardSchemaResolver(assignmentFeedbackSchema),
    defaultValues: {
      overall: submission.feedback?.overall ?? '',
      files: Object.fromEntries(submission.files.map((file) => [file.id, submission.feedback?.files[file.id] ?? ''])),
    },
  });

  const onSubmit = (feedback: AssignmentFeedbackSchema) =>
    mutateAsync({ assignmentId, responseId: submission.id, feedback })
      .then(() => toast.success('Zapisano informację zwrotną'))
      .catch(() => toast.error(errorMessage.title, { description: errorMessage.detail }));

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="border-border-primary bg-muted/50 dark:bg-card/75 flex flex-col gap-4 rounded-lg border p-3">
        <p className="text-muted-foreground text-sm font-medium">Informacja zwrotna</p>

        <FormField
          control={form.control}
          name="overall"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Komentarz ogólny</FormLabel>
              <FormControl>
                <FeedbackEditor value={field.value} onChange={field.onChange} placeholder="Komentarz do całej odpowiedzi" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {submission.files.map((file) => (
          <FormField
            key={file.id}
            control={form.control}
            name={`files.${file.id}`}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between gap-2">
                  <FormLabel className="min-w-0 truncate">Komentarz do pliku: {file.name}</FormLabel>
                  <AttachmentPreviewButton id={file.id} name={file.name} className="shrink-0" />
                </div>
                <FormControl>
                  <FeedbackEditor value={field.value} onChange={field.onChange} placeholder="Komentarz do pliku" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}

        <Button type="submit" variant="secondary" size="sm" className="w-fit" disabled={isPending}>
          Zapisz informację zwrotną
        </Button>
      </form>
    </Form>
  );
}

interface FeedbackEditorProps {
  value: string | null;
  onChange: (value: string) => void;
  placeholder: string;
}

function FeedbackEditor({ value, onChange, placeholder }: FeedbackEditorProps) {
  return (
    <div className="border-border-primary bg-background rounded-md border">
      <Tiptap extensions={simpleExtensionSet} className="p-3" placeholder={placeholder} content={value} onUpdate={(res) => onChange(res.editor.isEmpty ? '' : res.editor.getHTML())}>
        <FeedbackEditorToolbar />
      </Tiptap>
    </div>
  );
}

function FeedbackEditorToolbar() {
  const buttonClassName = 'h-8 min-h-8 w-8 min-w-8';
  return (
    <div className="bg-card/50 sticky top-0 z-20 flex flex-wrap items-center gap-0.5 border-b p-2">
      <UndoRedoButton type="button" action="undo" size="sm" variant="ghost" className={buttonClassName} />
      <UndoRedoButton type="button" action="redo" size="sm" variant="ghost" className={buttonClassName} />
      <MarkButton type="bold" size="sm" variant="ghost" className={buttonClassName} />
      <MarkButton type="italic" size="sm" variant="ghost" className={buttonClassName} />
      <ListDropdownMenu type="button" size="sm" variant="ghost" className="h-8 min-h-8" types={['bulletList', 'orderedList']} />
    </div>
  );
}
